import React, { useState } from 'react'
import { Bot } from 'lucide-react'
import { useUser } from 'src/shared/context/UserContext'
import { Channel, CustomerData, LedgerDetails } from './types'
import { getRiskConfig, buildFallbackMessage } from './riskConfig'
import RiskBanner from './RiskBanner'
import CreditLimitControl from './CreditLimitControl'
import NotificationComposer from './NotificationComposer'
import PendingApprovalsCard from './PendingApprovalsCard'
import { useCreditLimitRequest } from './hooks/useCreditLimitRequest.mock'
import { useMessageDraft } from './hooks/useMessageDraft.mock'
import { useIsMobileLayout } from '../details/LedgerData'

export interface LedgerAiControlProps {
    orderId: string
    currency?: string
    customerData: CustomerData
    ledgerDetails: LedgerDetails
    isCreditControlEnabled?: boolean
    onUpdateCreditLimit: (customerId: string, newLimit: number) => Promise<void>
    pendingApprovalsCount?: number
    onViewPendingApprovals?: () => void
}

export default function LedgerAiControl({
    orderId,
    customerData,
    ledgerDetails,
    currency = 'QAR',
    isCreditControlEnabled = false,
    onUpdateCreditLimit,
    pendingApprovalsCount = 2,
    onViewPendingApprovals,
}: LedgerAiControlProps) {
    const { user } = useUser()
    const canManageCreditLimit = user?.role?.[0] === 'Owner' || user?.role?.[0] === 'Admin'

    const order = { id: orderId, customerName: customerData.customerName, amount: ledgerDetails.orderAmount, currency }
    const fmt = (n: number): string => `${n.toFixed(2)} ${currency}`

    const {
        creditLimit,
        draftLimit,
        request,
        isSubmitting,
        validationError,
        handleDraftChange,
        bump,
        submit: submitLimitChange,
    } = useCreditLimitRequest({
        customerId: customerData.customerId,
        initialLimit: ledgerDetails.creditLimit,
        requestedByName: user && 'name' in user ? String(user.name) : undefined,
        requestedByRole: user?.role?.[0],
        onUpdateCreditLimit,
    })

    const projected = ledgerDetails.outstandingAmount
    const available = creditLimit - ledgerDetails.outstandingAmount
    const percentUsed = creditLimit > 0 ? (projected / creditLimit) * 100 : 0
    const risk = getRiskConfig(percentUsed)

    const { messages, loading, error, generate } = useMessageDraft({
        order,
        outstandingAmount: ledgerDetails.outstandingAmount,
        creditLimit,
        percentUsed,
        risk,
        fmt,
    })

    const [activeTab, setActiveTab] = useState<Channel>('whatsapp')
    const [sent, setSent] = useState(false)

    const handleTabClick = (key: Channel) => {
        setActiveTab(key)
        setSent(false)
        if (!messages[key]) generate(key)
    }

    const handleRegenerate = () => {
        setSent(false)
        generate(activeTab)
    }

    const isMobile = useIsMobileLayout();

    const currentMessage = messages[activeTab] ?? buildFallbackMessage(activeTab, order)
    const recipient = activeTab === 'email' ? customerData.email || '—' : customerData.whatsappNo

    return (
        <div className={`min-h-fit w-full flex items-start font-sans ${isMobile ? "mt-16 justify-end" : "justify-center bg-slate-100"}`}>
            <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm ring-1 ring-slate-200 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 pt-4 pb-3">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-indigo-950 flex items-center justify-center shrink-0">
                            <Bot size={17} className="text-white" strokeWidth={2} />
                        </div>
                        <div>
                            <p className="text-[13px] font-semibold text-slate-900 leading-tight">Majlis Assistant</p>
                            <p className="text-[11px] text-slate-400 leading-tight">Credit control</p>
                        </div>
                    </div>
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${risk.badgeText} ${risk.badgeBg}`}>{risk.badgeLabel}</span>
                </div>

                <div className="px-4 pb-4 space-y-3">
                    <RiskBanner
                        risk={risk}
                        percentUsed={percentUsed}
                        outstandingAmount={ledgerDetails.outstandingAmount}
                        creditLimit={creditLimit}
                        available={available}
                        fmt={fmt}
                    />

                    {isCreditControlEnabled && (
                        <CreditLimitControl
                            currency={currency}
                            draftLimit={draftLimit}
                            creditLimit={creditLimit}
                            request={request}
                            isSubmitting={isSubmitting}
                            validationError={validationError}
                            canManageCreditLimit={canManageCreditLimit}
                            onDraftChange={handleDraftChange}
                            onBump={bump}
                            onSubmit={() => {
                                // Only Owner and Admin can update the ledger credit limit.
                                if (canManageCreditLimit) {
                                    submitLimitChange()
                                    onUpdateCreditLimit(customerData.customerObjId, parseFloat(draftLimit))
                                } else {
                                    // User requests are disabled until the approval flow is available.
                                    // submitLimitChange()
                                }
                            }}
                            fmt={fmt}
                        />
                    )}

                    <NotificationComposer
                        activeTab={activeTab}
                        onTabChange={handleTabClick}
                        recipient={recipient}
                        currentMessage={currentMessage}
                        loading={loading}
                        error={error}
                        sent={sent}
                        onRegenerate={handleRegenerate}
                        onSend={() => setSent(true)}
                    />

                    {/* <PendingApprovalsCard count={pendingApprovalsCount} onClick={onViewPendingApprovals} /> */}
                </div>
            </div>
        </div>
    )
}
